import { useEffect, useMemo, useState } from "react";
import {
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  PhoneArrowUpRightIcon,
} from "@heroicons/react/24/outline";
import { Button } from "V2.0/common/button";
import { message } from "antd";

import VoiceEmbed from "../Embed/Voice";
import useWidget from "./useWidget";
import useWhitelistDomain from "../ChatEmbed/useWhitelistDomain";
import Domains from "../ChatEmbed/Domains";
import WhitelistedDomainsTable from "../ChatEmbed/WhitelistedDomainsTable";
import WidgetConfigurationCustomize from "./WidgetConfigurationCustomize";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

export default VoiceWidget;

function getVoiceWidgetCode(workspaceId: string, assistantId: string): string {
  return `<script>
      (function (w, d, s, o, f, js, fjs) {
        w[o] =
          w[o] ||
          function () {
            (w[o].q = w[o].q || []).push(arguments);
          };

        ((js = d.createElement(s)), (fjs = d.getElementsByTagName(s)[0]));

        js.id = o;
        js.src = f;
        js.async = 1;

        fjs.parentNode.insertBefore(js, fjs);
      })(
        window,
        document,
        "script",
        "_hw",
        "https://${window.location.hostname}/voice-widget.js",
      );
      _hw("init", { debug: true, workspaceId: "${workspaceId}", assistantId: "${assistantId}" });
    </script>`;
}

interface VoiceWidgetProps {
  workspaceId: string;
  assistantId: string;
  locked: boolean;
}

function VoiceWidget(props: VoiceWidgetProps) {
  const { workspaceId, assistantId, locked } = props;

  const { data, isLoading, isCreated, createNewWidget, fetchWidget } =
    useWidget(workspaceId, assistantId);

  const {
    data: whitelistDomainData,
    isLoading: isWhitelistDomainLoading,
    isCreated: isWhitelistDomainCreated,
    handleCreateDomainIdentifier,
    handleUpdateDomainIdentifiers,
  } = useWhitelistDomain(workspaceId, assistantId);

  const [openWidgetConfiguration, setOpenConfiguration] =
    useState<boolean>(false);

  useEffect(() => {
    fetchWidget(true);
  }, []);

  useEffect(() => {
    if (!isLoading && !isCreated) {
      createNewWidget();
    }
  }, [isLoading, isCreated]);

  const voiceWidgetCode = useMemo(() => {
    return getVoiceWidgetCode(workspaceId, assistantId);
  }, [workspaceId, assistantId]);

  const updatedWidgetData = useMemo(() => {
    if (data) {
      const { title, ...restData } = data;
      return {
        title,
      };
    }

    return data;
  }, [data]);

  const handleCopy = (item, title) => {
    navigator.clipboard
      .writeText(item)
      .then(() => {
        message.success(`${title} copied`);
      })
      .catch((err) => {
        message.error("Failed to Copy Code");
      });
  };

  const widgetHeaderRender = (
    <div className="flex justify-between">
      <p className="font-semibold text-md">Widget Settings</p>

      <div className="flex-none gap-x-2">
        <Button
          outline
          disabled={isLoading || locked}
          onClick={() => {
            setOpenConfiguration(true);
          }}
        >
          <Cog6ToothIcon /> Customize Widget
        </Button>
      </div>
    </div>
  );

  const widgetCodeCopyRender = (
    <div className="flex items-center justify-between  border rounded-lg p-2">
      <div className="flex items-center gap-x-2">
        <PhoneArrowUpRightIcon className="size-4" />
        <p className="text-sm font-semibold">Voice Widget Code</p>
      </div>
      <Button
        plain
        disabled={locked}
        onClick={() => {
          handleCopy(voiceWidgetCode, "Voice Widget");
        }}
      >
        <DocumentDuplicateIcon /> Copy
      </Button>
    </div>
  );

  const domainsEmptyRender = (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col gap-y-4">
        <p className="text-sm text-gray-600">No Domains added</p>
      </div>
    </div>
  );

  const domainsRender = (
    <>
      <div className="flex items-center gap-x-2">
        <GlobeAltIcon className="size-4" />
        <p className="text-sm font-semibold">
          Domains ({whitelistDomainData.domainIdentifiers.length})
        </p>
      </div>
      <Domains
        domainIdentifiers={whitelistDomainData.domainIdentifiers}
        handleAddDomainIdentifier={
          isWhitelistDomainCreated
            ? handleUpdateDomainIdentifiers
            : handleCreateDomainIdentifier
        }
        locked={locked}
      />
    </>
  );

  const domainsTableRender = (
    <div className="flex-1 h-full min-h-0 overflow-auto">
      <WhitelistedDomainsTable
        domainIdentifiers={whitelistDomainData.domainIdentifiers}
      />
    </div>
  );

  const isWhitelistDomainAvailable =
    isWhitelistDomainCreated &&
    whitelistDomainData.domainIdentifiers.length > 0;

  return (
    <>
      <div className="flex flex-col min-h-0 mx-auto h-full w-2/5 gap-y-4 p-8">
        {widgetHeaderRender}
        {widgetCodeCopyRender}
        <div className="flex flex-col gap-y-4 h-full min-h-0 border rounded-lg p-4">
          {domainsRender}
          {isWhitelistDomainLoading ? (
            <div className="flex items-center justify-center flex-1">
              <AiOutlineLoading3Quarters className="animate-spin" />
            </div>
          ) : isWhitelistDomainAvailable ? (
            domainsTableRender
          ) : (
            domainsEmptyRender
          )}
        </div>
      </div>
      {openWidgetConfiguration && (
        <WidgetConfigurationCustomize
          open={openWidgetConfiguration}
          onClose={() => setOpenConfiguration(false)}
        >
          <VoiceEmbed
            data={updatedWidgetData}
            fetchWidget={fetchWidget}
            onClose={() => setOpenConfiguration(false)}
            workspaceId={workspaceId}
            assistantId={assistantId}
          />
        </WidgetConfigurationCustomize>
      )}
    </>
  );
}
